import { z } from 'zod';
import { categoryKeySchema } from './enums';
/**
 * A sub-category — one of the 5 fixed, non-configurable types, scoped to a
 * single main category. `key` is unique per main category, not globally: two
 * different main categories each have their own "Bug" row.
 */
export const categorySchema = z.object({
    id: z.string(),
    mainCategoryId: z.string(),
    key: categoryKeySchema,
    name: z.string(),
    description: z.string().nullable(),
    sortOrder: z.number().int(),
    isActive: z.boolean(),
});
/**
 * What an administrator may change from the website.
 *
 * `key` is deliberately absent — the 5 sub-category types are fixed, not
 * something an admin names. Everything here is presentation, and changing it
 * needs no deployment.
 *
 * There is no delete either. Deactivating hides a category from the composer
 * while leaving its existing topics readable; deleting one would orphan them.
 */
export const updateCategorySchema = z
    .object({
    name: z.string().trim().min(1, 'Name is required').max(128).optional(),
    description: z.string().trim().max(1024).nullable().optional(),
    sortOrder: z.number().int().min(0).max(999).optional(),
    isActive: z.boolean().optional(),
})
    .refine((patch) => Object.keys(patch).length > 0, {
    message: 'Provide at least one field to update',
});
/**
 * A main category — admin-configurable (create/rename/deactivate), unlike
 * its 5 sub-categories. Every main category gets its own copy of those 5,
 * generated automatically when it's created — see `DEFAULT_CATEGORIES` below.
 */
export const mainCategorySchema = z.object({
    id: z.string(),
    name: z.string(),
    sortOrder: z.number().int(),
    isActive: z.boolean(),
});
export const createMainCategorySchema = z.object({
    name: z.string().trim().min(1, 'Name is required').max(128),
    sortOrder: z.number().int().min(0).max(999).default(0),
});
export const updateMainCategorySchema = z
    .object({
    name: z.string().trim().min(1, 'Name is required').max(128).optional(),
    sortOrder: z.number().int().min(0).max(999).optional(),
    isActive: z.boolean().optional(),
})
    .refine((patch) => Object.keys(patch).length > 0, {
    message: 'Provide at least one field to update',
});
/**
 * The five sub-categories from the Business Partner Forum requirements, and
 * the single source of truth for them.
 *
 * This is no longer seeded on startup — it's the fixed template every main
 * category gets, all 5 rows, the moment an administrator creates it (see
 * `MainCategoryRepository.create`). Names and descriptions below are the
 * requirements doc's wording; an administrator can still edit them freely
 * per main category from here on. `key` cannot be renamed or reassigned —
 * it's what makes these "sub-categories" rather than just more main
 * categories.
 */
export const DEFAULT_CATEGORIES = [
    {
        key: 'Enhancement',
        name: 'Enhancements & New Ideas',
        description: 'Suggestions for improving existing functionality or introducing new features.',
        sortOrder: 1,
    },
    {
        key: 'Bug',
        name: 'Bugs & Problems',
        description: 'Discussion of issues experienced within MortgageMAX360.',
        sortOrder: 2,
    },
    {
        key: 'Compliment',
        name: 'Compliments & Positive Feedback',
        description: 'Recognition of functionality, initiatives, or service that adds value.',
        sortOrder: 3,
    },
    {
        key: 'Struggle',
        name: 'User Struggles & Pain Points',
        description: 'Areas where users experience challenges or friction. Not an HR grievance channel — see the forum terms.',
        sortOrder: 4,
    },
    {
        key: 'General',
        name: 'General Discussion',
        description: 'Mortgage industry discussions, best practices and collaboration among business partners.',
        sortOrder: 5,
    },
];
//# sourceMappingURL=categories.js.map